import { FC, useEffect, useMemo, useState, type DragEvent } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { getCompanyList, getVesselTypeList } from '../core/libraryFilters_requests'
import AddReportModal, { type AddReportModalProps } from './library/AddReportModal'
import { Reports, Fields, AssignedReports, CompanyAdmin } from '../core/libraryModels'
import AddFieldModal, { type AddFieldModalProps } from './library/AddFieldModal'
import AddTabModal, { type AddTabModalProps } from './library/AddTabModal'
import EditFieldModal, { type EditFieldModalProps } from './library/EditFieldModal'
import DeleteFieldModal, { type DeleteFieldModalProps } from './library/DeleteFieldModal'
import EditReportModal, { type EditReportModalProps } from './library/EditReportModal'
import EditTabModal, { type EditTabModalProps } from './library/EditTabModal'
import { getCompanyAdminList } from '../../Management/core/_requests'
import { useAuth } from '../../auth'
import { qhseLibraryApi } from '../core/libraryApi'
import { QHSE_PERMS } from '../core/permissions'
import {
    getUserCompanyAdminId,
    getUserCompanyGroupAdminId,
    isBroadFleetPickerUser,
} from '../core/qhseUserContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { SidebarTreeSkeleton, TableSkeleton } from '../../../shared/components/LoadingSkeletons'

type QhseLibraryLayoutProps = {
    title?: string
}

const QhseLibraryLayout: FC<QhseLibraryLayoutProps> = ({ title = 'Forms & Checklists Library' }) => {
    const libraryApi = qhseLibraryApi
    type WithoutApi<P extends { api: unknown }> = Omit<P, 'api'>
    const QhseAddReportModal: FC<WithoutApi<AddReportModalProps>> = (props) => (
        <AddReportModal {...props} api={libraryApi} />
    )
    const QhseEditReportModal: FC<WithoutApi<EditReportModalProps>> = (props) => (
        <EditReportModal {...props} api={libraryApi} />
    )
    const QhseAddTabModal: FC<WithoutApi<AddTabModalProps>> = (props) => (
        <AddTabModal {...props} api={libraryApi} />
    )
    const QhseEditTabModal: FC<WithoutApi<EditTabModalProps>> = (props) => (
        <EditTabModal {...props} api={libraryApi} />
    )
    const QhseAddFieldModal: FC<WithoutApi<AddFieldModalProps>> = (props) => (
        <AddFieldModal {...props} api={libraryApi} />
    )
    const QhseEditFieldModal: FC<WithoutApi<EditFieldModalProps>> = (props) => (
        <EditFieldModal {...props} api={libraryApi} />
    )
    const QhseDeleteFieldModal: FC<WithoutApi<DeleteFieldModalProps>> = (props) => (
        <DeleteFieldModal {...props} api={libraryApi} />
    )
    const { currentUser, tenant, roleDetails, can, permissionsLoaded } = useAuth()
    const { state: globalState, setCompany } = useGlobalFilter()
    const broadFleet = isBroadFleetPickerUser(currentUser, tenant, roleDetails)
    const myCgaId = getUserCompanyGroupAdminId(currentUser, tenant)
    const myCaId = getUserCompanyAdminId(currentUser, tenant)
    const subcompanyScoped = !broadFleet && myCaId != null && myCgaId == null
    const actsAsSuperadmin = broadFleet
    const actsAsCga = !broadFleet && myCgaId != null && !subcompanyScoped
    const effectiveCgaId = actsAsCga ? myCgaId : undefined
    const [mode, setMode] = useState<'master' | 'company'>(libraryApi.defaultMode);
    const [companies, setCompanies] = useState<{ id: number, name: string }[]>([]);
    const [vessels, setVessels] = useState<{ id: number, name: string, imoNumber: string }[]>([]);
    const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([]);
    const [selectedVessel, setSelectedVessel] = useState<{ id: number, name: string, imoNumber: string }>({ id: 0, name: "", imoNumber: "" });
    const [selectedCompany, setSelectedCompany] = useState<{ id: number, name: string }>(() => {
        if (globalState.company?.type === 'company_admin') {
            return { id: Number(globalState.company.id), name: globalState.company.name };
        }
        if (subcompanyScoped && myCaId != null) {
            return { id: myCaId, name: '' };
        }
        return { id: 0, name: '' };
    });
    const [selectedCompanyAdmin, setSelectedCompanyAdmin] = useState<{ id: number, name: string }>(() => {
        if (globalState.company?.type === 'company_group') {
            return { id: Number(globalState.company.id), name: globalState.company.name };
        }
        if (effectiveCgaId) {
            return { id: effectiveCgaId, name: '' };
        }
        return { id: 0, name: '' };
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [vesselTypes, setVesselTypes] = useState<string[]>([]);
    const [selectedVesselType, setSelectedVesselType] = useState<string>("");
    const [isAddReportModalOpen, setIsAddReportModalOpen] = useState(false);
    const [isAddTabModalOpen, setIsAddTabModalOpen] = useState(false);
    const [isEditReportModalOpen, setIsEditReportModalOpen] = useState(false);
    const [isEditTabModalOpen, setIsEditTabModalOpen] = useState(false);
    const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
    const [isEditFieldModalOpen, setIsEditFieldModalOpen] = useState(false);
    const [isDeleteFieldModalOpen, setIsDeleteFieldModalOpen] = useState(false);
    const [reports, setReports] = useState<Reports[]>([]);
    const [fields, setFields] = useState<Fields[]>([]);
    const [selectedField, setSelectedField] = useState<Fields>();
    const [menuId, setMenuId] = useState(0);
    const [submenuId, setSubmenuId] = useState(0);
    const [reorderMode, setReorderMode] = useState(false);
    const [draggingFieldId, setDraggingFieldId] = useState<number | null>(null);
    const [dragOverFieldId, setDragOverFieldId] = useState<number | null>(null);
    const [isReorderSaving, setIsReorderSaving] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10); // Default to 10
    const [isFieldAdded, setIsFieldAdded] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [shouldUseCompanyGroupAdmin, setShouldUseCompanyGroupAdmin] = useState(false);
    const sortFieldsByOrder = (list: Fields[]) => {
        return [...list].sort((a, b) => {
            const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
            const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
            if (orderA === orderB) return (a.id ?? 0) - (b.id ?? 0);
            return orderA - orderB;
        });
    };

    // Pagination logic
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const pagedFields = reorderMode ? fields : fields.slice(indexOfFirstRow, indexOfLastRow);
    const displayFields = pagedFields.filter(field => field.isActive);

    const totalPages = Math.ceil(fields.length / rowsPerPage);

    const activeReport = useMemo(
        () => reports.find(report => report.submenus?.some(tab => tab.id === submenuId)),
        [reports, submenuId]
    );
    const activeFieldCount = useMemo(() => fields.filter(field => field.isActive).length, [fields]);
    const editAllowedByMode = libraryApi.features.allowEditInCompanyMode || mode === 'master'
    const canMutateLibrary =
        permissionsLoaded &&
        (can(QHSE_PERMS.FORM_MENU_CREATE) ||
            can(QHSE_PERMS.FORM_MENU_UPDATE) ||
            can(QHSE_PERMS.FORM_SUBMENU_CREATE) ||
            can(QHSE_PERMS.FORM_SUBMENU_UPDATE) ||
            can(QHSE_PERMS.FORM_FIELD_CREATE) ||
            can(QHSE_PERMS.FORM_FIELD_UPDATE) ||
            can(QHSE_PERMS.FORM_FIELD_DELETE))
    const canEdit = editAllowedByMode && canMutateLibrary
    const canReorderFields =
        permissionsLoaded && editAllowedByMode && can(QHSE_PERMS.FORM_FIELD_UPDATE)
    const emptyColSpan = useMemo(
        () => (canEdit ? 8 : 7) + (reorderMode ? 1 : 0),
        [canEdit, reorderMode]
    );

    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    useEffect(() => {
        if (actsAsSuperadmin) {
            fetchCompanyAdmins();
        }
        if (selectedCompanyAdmin.id) {
            fetchCompaniesbyCompanyAdmin(selectedCompanyAdmin.id);
        }
    }, []);

    useEffect(() => {
        if (globalState.company) {
            if (globalState.company.type === 'company_group') {
                const cgaId = Number(globalState.company.id);
                setSelectedCompanyAdmin(prev => {
                    if (prev.id === cgaId) return prev;
                    return { id: cgaId, name: globalState.company!.name };
                });
                setSelectedCompany({ id: 0, name: '' });
                fetchCompaniesbyCompanyAdmin(cgaId);
            } else if (globalState.company.type === 'company_admin') {
                const caId = Number(globalState.company.id);
                setSelectedCompany(prev => {
                    if (prev.id === caId) return prev;
                    return { id: caId, name: globalState.company!.name };
                });
            }
        } else if (actsAsSuperadmin) {
            setSelectedCompanyAdmin(prev => (prev.id === 0 ? prev : { id: 0, name: '' }));
            setSelectedCompany(prev => (prev.id === 0 ? prev : { id: 0, name: '' }));
            setCompanies([]);
        }
    }, [globalState.company, actsAsSuperadmin]);

    useEffect(() => {
  if (actsAsCga && effectiveCgaId) {
    setSelectedCompanyAdmin({ id: effectiveCgaId, name: '' });
    fetchCompaniesbyCompanyAdmin(effectiveCgaId);
  }

  if (subcompanyScoped && myCaId != null) {
    setSelectedCompany({ id: myCaId, name: '' });
    if (libraryApi.features.showVesselFilters) {
        fetchVesselTypeByCompany(myCaId);
    }
  }
}, [actsAsCga, effectiveCgaId, subcompanyScoped, myCaId, libraryApi.features.showVesselFilters]);


    useEffect(() => {
        if (reports.length > 0 && reports[0]?.submenus?.length > 0) {
            if (isFieldAdded && submenuId) {
                handleTabClick(submenuId); // Keep current tab if field was added
                setIsFieldAdded(false); // Reset the flag
            } else {
                const firstTabId = reports[0].submenus[0].id;
                handleTabClick(firstTabId); // Default to first tab
                setSubmenuId(firstTabId);
            }
        }
    }, [reports]);

    useEffect(() => {
        fetchReportsList();
    }, [selectedCompanyAdmin, selectedCompany, selectedVesselType, selectedVessel]);

    const handleFieldAdded = async () => {
        setIsFieldAdded(true);
        const currentTabId = submenuId;
        await fetchReportsList();
        setSubmenuId(currentTabId); // Reapply selected tab
    };

    const onReportAdded = async (tempId: number) => {
        fetchReportsList();
        if (!libraryApi.features.useAssignments || !libraryApi.assignReport) {
            return;
        }
        if (!permissionsLoaded || !can(QHSE_PERMS.FORM_MENU_CREATE)) {
            return;
        }
        try {
            if (actsAsSuperadmin) {
                for (const companyAdmin of companyAdmins) {
                    try {
                        await libraryApi.assignReport(companyAdmin.id, tempId);
                    } catch (err) {
                        console.error(`Failed to assign template to company Admin ID ${companyAdmin.id}:`, err);
                    }
                }
            } else if (actsAsCga) {
                try {
                    await libraryApi.assignReport(selectedCompanyAdmin.id, tempId);
                } catch (err) {
                    console.error(`Failed to assign template to company Admin ID ${selectedCompanyAdmin.id}:`, err);
                }
            }
        } catch (err) {
            console.error('Failed to assign template:', err);
        }
    };

    const fetchCompanyAdmins = async () => {
        try {
            const companyAdminList = await getCompanyAdminList();

            const filteredCompanies = companyAdminList.filter(company => company.active === true);

            setCompanyAdmins(filteredCompanies);
        } catch (error) {
            console.error('Failed to fetch company admin list:', error);
        }
    };

    const fetchCompaniesbyCompanyAdmin = async (companyAdminId: number) => {
        try {
            const companyList = await getCompanyList();

            const filteredCompanies = companyList.filter(company => company.cga?.id === companyAdminId && company.active === true);

            setCompanies(filteredCompanies);
            return filteredCompanies;
        } catch (error) {
            console.error('Failed to fetch company list:', error);
            return [];
        }
    };

    const fetchVesselTypeByCompanyAdmin = async (companyAdminId: number) => {

        try {
            const vesselList = await getVesselTypeList();

            const filteredVessels = vesselList.filter(
                (vessel) => vessel.companyGroupAdmin?.id === companyAdminId
            );

            // Extract vesselType names (including duplicates, if needed)
            const vesselTypeNames = filteredVessels.map(v => v.vesselType);

            // Optional: remove duplicates if you only want distinct names
            const distinctVesselTypes = Array.from(new Set(vesselTypeNames));
            setVesselTypes(distinctVesselTypes);

        } catch (error) {
            console.error('Failed to fetch vesseltype by company list:', error);
        }
    };

    const fetchVesselTypeByCompany = async (companyId: number) => {

        try {
            const vesselList = await getVesselTypeList();

            const filteredVessels = vesselList.filter(
                (vessel) => vessel.companyAdmin?.id === companyId
            );

            // Extract vesselType names (including duplicates, if needed)
            const vesselTypeNames = filteredVessels.map(v => v.vesselType);

            // Optional: remove duplicates if you only want distinct names
            const distinctVesselTypes = Array.from(new Set(vesselTypeNames));
            setVesselTypes(distinctVesselTypes);


        } catch (error) {
            console.error('Failed to fetch vesseltype by company list:', error);
        }
    };

    const fetchVesselsByVesselType = async (vesselType: string, companyId: number) => {

        try {
            const vesselList = await getVesselTypeList();
            // Filter vessels by vesselType
            const filteredVessels = vesselList.filter(
                (vessel) => {
                    if (shouldUseCompanyGroupAdmin) {
                        return (
                            vessel.vesselType === vesselType &&
                            vessel.companyGroupAdmin?.id === selectedCompanyAdmin.id
                        );
                    } else {
                        return (
                            vessel.vesselType === vesselType &&
                            vessel.companyAdmin?.id === companyId
                        );
                    }
                }
            );

            // Extract only required fields: imoNumber, fleet_name, and id
            const vesselDetails = filteredVessels.map((vessel) => ({
                id: vessel.id,
                name: vessel.fleet_name,
                imoNumber: vessel.imoNumber,
            }));

            setVessels(vesselDetails);

        } catch (error) {
            console.error('Failed to fetch vessel by vesseltype list:', error);
        }
    };

    const handleTabClick = async (tabId: number) => {
        try {

            // Flatten all submenus from all reports
            const allSubmenus = reports.flatMap(report => report.submenus);

            // Find the submenu (tab) that matches the given tabId
            const targetTab = allSubmenus.find(submenu => submenu.id === tabId);

            if (targetTab) {
                // Set its fields to state
                setFields(sortFieldsByOrder(targetTab.fields || []));
                setCurrentPage(1);            // ✅ Reset pagination to first page
            } else {
                console.warn(`No tab found with ID ${tabId}`);
                setFields([]);
                setCurrentPage(1);            // ✅ Also reset page here to avoid stale pagination
            }

            // const allfields = await getFieldsForTab(auth.jwt, tabId);

            // setFields(allfields);

        } catch (err) {
            console.error('Failed to fetch fields list:', err);
            return [];
        }
    };

    const fetchReportsList = async () => {
        try {
            setLoading(true);

            let templateIDs: number[] = [];
            let filteredList: any[] = [];
            let allReports: any[] = [];

            const useAssignments = libraryApi.features.useAssignments && mode === "company";
            if (!useAssignments || selectedCompanyAdmin?.id === 0) {
                const fetched = await fetchTemplateIds();
                templateIDs = fetched ?? []; // fallback to [] if undefined
            } else {
                const templateList = await fetchAssignedTemplateIds();
                filteredList = templateList; // Save for later customTemplate.menus use

                templateIDs = templateList
                    .filter(item => item.customTemplate == null && item.template != null)
                    .map(item => item.template!.id);

            }

            // ✅ Add customTemplate.menus as additional "reports"
            const customMenus = filteredList
                .filter(item => item.customTemplate && Array.isArray(item.customTemplate.menus))
                .flatMap(item => item.customTemplate!.menus!);


            if (templateIDs && templateIDs.length > 0) {
                const reportsPerTemplateResults = await Promise.allSettled(
                    templateIDs.map(templateId => libraryApi.getReportsForTemplate(templateId))
                );

                const reportsPerTemplate = reportsPerTemplateResults
                    .filter(result => result.status === 'fulfilled')
                    .map(result => (result as PromiseFulfilledResult<Reports[]>).value);

                allReports = reportsPerTemplate.flat();
            } else {
                console.warn('No template IDs found.');
            }
            allReports = [...allReports, ...customMenus];

            setReports(allReports); // ✅ Set state directly
            if (allReports.length === 0) {
                setFields([]);
                setSubmenuId(0);
            }
        } catch (err) {
            console.error('Failed to fetch reports list:', err);
            setReports([]);
            setFields([]);
            setSubmenuId(0);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const persistFieldOrder = async (orderedFields: Fields[]) => {
        if (!orderedFields.length || !submenuId) return;
        const customTemplateId = activeReport?.customTemplateId;
        try {
            setIsReorderSaving(true);
            await Promise.all(
                orderedFields.map((field, index) => {
                    const order = index + 1;
                    const options = field.optionsJson && field.optionsJson.length ? field.optionsJson : undefined;
                    if (customTemplateId != null && libraryApi.updateCustomField) {
                        return libraryApi.updateCustomField(
                            customTemplateId,
                            field.id,
                            field.label,
                            field.fieldType,
                            field.readOnly,
                            field.required,
                            field.warningOnly ?? undefined,
                            field.isActive,
                            field.unit,
                            field.minValue,
                            field.maxValue,
                            options,
                            order
                        );
                    }
                    return libraryApi.updateField(
                        field.submenuId ?? submenuId,
                        field.id,
                        field.label,
                        field.fieldType,
                        field.required,
                        field.readOnly,
                        field.warningOnly ?? undefined,
                        field.isActive,
                        field.unit,
                        field.minValue,
                        field.maxValue,
                        options,
                        order
                    );
                })
            );
            setIsFieldAdded(true);
            await fetchReportsList();
            handleTabClick(submenuId);
        } catch (err) {
            console.error('Failed to update field order:', err);
        } finally {
            setIsReorderSaving(false);
        }
    };

    const applyFieldReorder = async (sourceId: number, targetId: number) => {
        if (sourceId === targetId) return;
        const activeFields = fields.filter(field => field.isActive);
        const inactiveFields = fields.filter(field => !field.isActive);
        const fromIndex = activeFields.findIndex(field => field.id === sourceId);
        const toIndex = activeFields.findIndex(field => field.id === targetId);
        if (fromIndex < 0 || toIndex < 0) return;
        const reorderedActive = [...activeFields];
        const [moved] = reorderedActive.splice(fromIndex, 1);
        reorderedActive.splice(toIndex, 0, moved);
        const orderedFields = reorderedActive.map((field, index) => ({
            ...field,
            order: index + 1,
        }));
        const combined = [...orderedFields, ...inactiveFields];
        setFields(combined);
        await persistFieldOrder(combined);
    };

    const handleDragStart = (fieldId: number) => {
        if (!reorderMode || isReorderSaving) return;
        setDraggingFieldId(fieldId);
    };

    const handleDragOver = (event: DragEvent<HTMLTableRowElement>, fieldId: number) => {
        if (!reorderMode) return;
        event.preventDefault();
        if (dragOverFieldId !== fieldId) {
            setDragOverFieldId(fieldId);
        }
    };

    const handleDrop = async (fieldId: number) => {
        if (!reorderMode || isReorderSaving || draggingFieldId === null) return;
        const sourceId = draggingFieldId;
        setDraggingFieldId(null);
        setDragOverFieldId(null);
        await applyFieldReorder(sourceId, fieldId);
    };

    const handleDragEnd = () => {
        setDraggingFieldId(null);
        setDragOverFieldId(null);
    };

    const resolveCompanyGroupId = () => {
        if (selectedCompanyAdmin?.id) {
            return selectedCompanyAdmin.id;
        }
        if (effectiveCgaId) {
            return effectiveCgaId;
        }
        if (myCgaId) {
            return Number(myCgaId);
        }
        if (globalState.company?.type === 'company_group') {
            return Number(globalState.company.id);
        }
        return undefined;
    };

    const resolveCompanyAdminId = () => {
        if (selectedCompany?.id) {
            return selectedCompany.id;
        }
        if (globalState.company?.type === 'company_admin') {
            return Number(globalState.company.id);
        }
        return undefined;
    };

    const fetchTemplateIds = async () => {
        try {

            const templateList = await libraryApi.getTemplateList({
                companyGroupAdminId: resolveCompanyGroupId(),
                companyAdminId: resolveCompanyAdminId(),
            });

            // Extract all IDs
            const templateIds: number[] = templateList
                .map(template => template.id);

            return templateIds;
        } catch (err) {
            console.error('Failed to fetch template list:', err);
        }
    };

    const fetchAssignedTemplateIds = async (): Promise<AssignedReports[]> => {
        try {

            let filteredList: any[] = [];

            if (selectedCompanyAdmin?.id !== 0 && selectedCompany?.id === 0 && selectedVesselType === '' && selectedVessel?.id === 0) {
                if (!libraryApi.getAssignedTemplatesByCompanyAdmin) {
                    return [];
                }
                filteredList = await libraryApi.getAssignedTemplatesByCompanyAdmin(selectedCompanyAdmin.id);
            } else if (selectedCompanyAdmin?.id !== 0 && selectedCompany.id !== 0 && selectedVesselType === '' && selectedVessel?.id === 0) {
                if (!libraryApi.getAssignedTemplatesByCompany) {
                    return [];
                }
                filteredList = await libraryApi.getAssignedTemplatesByCompany(selectedCompanyAdmin.id, selectedCompany.id);
            } else if (selectedCompanyAdmin?.id !== 0 && selectedVesselType !== '' && selectedVessel?.id === 0) {
                if (!libraryApi.getAssignedTemplatesByVesselType) {
                    return [];
                }
                filteredList = await libraryApi.getAssignedTemplatesByVesselType(selectedCompanyAdmin?.id, selectedCompany.id, selectedVesselType);
            } else if (selectedCompanyAdmin?.id !== 0 && selectedVesselType !== '' && selectedVessel?.id !== 0) {
                if (!libraryApi.getAssignedTemplatesByVesselId) {
                    return [];
                }
                filteredList = await libraryApi.getAssignedTemplatesByVesselId(selectedCompanyAdmin.id, selectedCompany.id, selectedVesselType, selectedVessel.id);
            }

            return filteredList;

        } catch (err) {
            console.error('Failed to fetch template list:', err);
            return [];
        }
    };

    return (
        <div
            className='app-main flex-column flex-row-fluid'
            style={{
                display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: 'calc(100vh - 110px)', overflow: 'hidden',
            }}
        >
            <div className='d-flex flex-column flex-column-fluid' style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }} >
                <div
                    className='app-content flex-column-fluid d-flex flex-column'
                    style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                >
                    <div className='card d-flex flex-column border-top'
                        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                    >
                        {/* Header */}

                        <div className='align-items-center justify-content-start py-4 gap-2 gap-md-12 px-5 bg-white'  >
                            <div>
                                <div className='d-flex align-items-center gap-1'>
                                    <h3 className='card-title fw-bold text-dark'>{title}</h3>
                                </div>
                                {libraryApi.features.showModeToggle && permissionsLoaded && can(QHSE_PERMS.FORM_TEMPLATE_READ) && (
                                    <div className='d-flex gap-12'>
                                    <div className='pb-2'>Mode
                                        <div className='d-flex align-items-center gap-2'>
                                            <input
                                                type="radio"
                                                name='mode'
                                                value='master'
                                                checked={mode === "master"}
                                                onChange={(e) => {
                                                    setMode(e.target.value as 'master' | 'company');
                                                    setVessels([]);
                                                    setVesselTypes([]);
                                                    setCompanies([]);
                                                    if (actsAsSuperadmin) {
                                                        setSelectedCompanyAdmin({ id: 0, name: "" });
                                                    }
                                                    setSelectedCompany({ id: 0, name: "" });
                                                    setSelectedVesselType("");
                                                    setSelectedVessel({ id: 0, name: "", imoNumber: "" });
                                                }}
                                            />
                                            <label>Master</label>
                                            <input
                                                type="radio"
                                                name='mode'
                                                value='company'
                                                checked={mode === "company"}
                                                onChange={(e) => {
                                                    setMode(e.target.value as 'master' | 'company');
                                                    if (globalState.company?.type === 'company_group') {
                                                        const cgaId = Number(globalState.company.id);
                                                        setSelectedCompanyAdmin({ id: cgaId, name: globalState.company.name });
                                                        setSelectedCompany({ id: 0, name: "" });
                                                        fetchCompaniesbyCompanyAdmin(cgaId);
                                                    } else if (globalState.company?.type === 'company_admin') {
                                                        const caId = Number(globalState.company.id);
                                                        setSelectedCompany({ id: caId, name: globalState.company.name });
                                                    } else if (actsAsSuperadmin) {
                                                        setSelectedCompanyAdmin({ id: 0, name: "" });
                                                        setSelectedCompany({ id: 0, name: "" });
                                                    }
                                                    setSelectedVesselType("");
                                                    setSelectedVessel({ id: 0, name: "", imoNumber: "" });
                                                }}
                                            />
                                            <label>Company</label>
                                        </div>
                                    </div>

                                </div>
                                )}
                                <div className='d-flex gap-12'>
                                    {
                                        mode === "company" && libraryApi.features.showCompanyFilters &&
                                        <>
                                            {actsAsSuperadmin && <div>
                                                Company
                                                <div>
                                                    <SearchableSelect
                                                        value={selectedCompanyAdmin.id}
                                                        onChange={(e) => {
                                                            setSelectedCompany({ id: 0, name: "" })
                                                            setSelectedVessel({ id: 0, name: "", imoNumber: "" });
                                                            setSelectedVesselType("")
                                                            const selectedId = parseInt(e.target.value, 10);
                                                            const selected = companyAdmins.find(c => c.id === selectedId);
                                                            setSelectedCompanyAdmin(selected || { id: 0, name: "" });
                                                            if (selected) {
                                                                setCompany({ id: selected.id, name: selected.name, type: 'company_group' });
                                                            } else {
                                                                setCompany(null);
                                                            }
                                                            // fetchCompaniesbyCompanyAdmin(selectedId);
                                                            fetchCompaniesbyCompanyAdmin(selectedId).then((companyList) => {
                                                                if (libraryApi.features.showVesselFilters) {
                                                                    if (companyList?.length === 0) {
                                                                        fetchVesselTypeByCompanyAdmin(selectedId); // fallback
                                                                        setShouldUseCompanyGroupAdmin(true);
                                                                    } else {
                                                                        fetchVesselTypeByCompany(0);
                                                                        setShouldUseCompanyGroupAdmin(false);
                                                                    }
                                                                }
                                                            });
                                                            if (libraryApi.features.showVesselFilters) {
                                                                fetchVesselsByVesselType("", 0);
                                                            }

                                                        }}
                                                        className='form-select'
                                                    >
                                                        <option value="">--Select Company--</option>
                                                        {companyAdmins.map((companyAdmin, index) => (
                                                            <option key={index} value={companyAdmin.id}>{companyAdmin.name}</option>
                                                        ))}
                                                    </SearchableSelect>
                                                </div>
                                            </div>}
                                            {(actsAsSuperadmin || actsAsCga) && <div>
                                                Sub Company
                                                <div>
                                                    <SearchableSelect
                                                        value={selectedCompany.id}
                                                        onChange={(e) => {
                                                            setSelectedVessel({ id: 0, name: "", imoNumber: "" });
                                                            setSelectedVesselType("")
                                                            const selectedId = parseInt(e.target.value, 10);
                                                            const selected = companies.find(c => c.id === selectedId);
                                                            setSelectedCompany(selected || { id: 0, name: "" });
                                                            if (selected) {
                                                                setCompany({ id: selected.id, name: selected.name, type: 'company_admin' });
                                                            } else if (selectedCompanyAdmin.id) {
                                                                setCompany({ id: selectedCompanyAdmin.id, name: selectedCompanyAdmin.name, type: 'company_group' });
                                                            }
                                                            if (libraryApi.features.showVesselFilters) {
                                                                fetchVesselTypeByCompany(selectedId)
                                                                fetchVesselsByVesselType("", 0);
                                                            }

                                                        }}
                                                        className='form-select'
                                                    >
                                                        <option value="">--Select Sub Company--</option>
                                                        {companies.map((company, index) => (
                                                            <option key={index} value={company.id}>{company.name}</option>
                                                        ))}
                                                    </SearchableSelect>
                                                </div>
                                            </div>}
                                            {libraryApi.features.showVesselFilters && (
                                                <>
                                                    <div>
                                                        Vessel Type
                                                        <div>
                                                            <SearchableSelect
                                                                onChange={(e) => {
                                                                    setSelectedVessel({ id: 0, name: "", imoNumber: "" });
                                                                    setSelectedVesselType(e.target.value)
                                                                    fetchVesselsByVesselType(e.target.value, selectedCompany?.id)
                                                                }}
                                                                className='form-select'
                                                            >
                                                                <option value="">Select Vessel Type</option>
                                                                {vesselTypes.map((type, index) => (
                                                                    <option key={index} value={type}>{type}</option>
                                                                ))}
                                                            </SearchableSelect>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        Vessel
                                                        <div>
                                                            <SearchableSelect
                                                                onChange={(e) => {
                                                                    const selectedId = parseInt(e.target.value, 10);
                                                                    const selected = vessels.find(v => v.id === selectedId);
                                                                    setSelectedVessel(selected || { id: 0, name: "", imoNumber: "" });
                                                                }}
                                                                className='form-select'
                                                            >
                                                                <option value="">Select Vessel</option>
                                                                {vessels.map((vessel) => (
                                                                    <option key={vessel.id} value={vessel.id}>
                                                                        {vessel.name} (IMO: {vessel.imoNumber})
                                                                    </option>
                                                                ))}
                                                            </SearchableSelect>
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                        </>}
                                </div>
                            </div>
                        </div>

                        <div className='d-flex flex-grow-1' style={{ minHeight: 0, overflow: 'hidden' }}>
                            <div className='px-3 pe-3 border-end py-3 bg-white' style={{ flex: 1, minWidth: '280px', maxWidth: '380px', minHeight: 0, overflowY: 'auto', paddingBottom: '3rem' }}>
                                <div className='d-flex justify-content-between align-items-center mb-1'>
                                    <h5 className='mb-0'>Reports</h5>
                                    {
                                        canEdit && (
                                            <div className='d-flex gap-2'>
                                                {reports.length !== 0 &&

                                                    <button
                                                        className="action-btn d-flex align-items-center justify-content-center"
                                                        onClick={() => { setIsEditReportModalOpen(true) }}
                                                    >
                                                        <KTSVG path='/media/map/settings.svg' className='svg-icon-1' />
                                                    </button>
                                                }
                                                <button
                                                    className="action-btn d-flex align-items-center justify-content-center"
                                                    style={{ color: '#1C325B' }}
                                                    onClick={() => { setIsAddReportModalOpen(true) }}
                                                >
                                                    <KTSVG path='/media/map/zoom-in.svg' className='svg-icon-1' />
                                                </button>
                                            </div>

                                        )
                                    }
                                </div>
                                {loading ? (
                                    <SidebarTreeSkeleton count={4} />
                                ) : reports.filter(report => report.isActive).length === 0 ? (
                                    <div className='text-center mt-6 text-muted'>No reports found</div>
                                ) : (
                                    reports.filter(report => report.isActive).map((report, index) => (
                                        <div key={report.id} className="accordion" id="reportAccordion">
                                            {/* <!-- Report Type 1 --> */}
                                            <div className="accordion-item">
                                                <h2 className="accordion-header" id={`heading-${report.id}`}>
                                                    <button className={`accordion-button ${index !== 0 ? 'collapsed' : ''}`}
                                                        type="button" data-bs-toggle="collapse" data-bs-target={`#report-${report.id}`}
                                                        aria-expanded={index === 0 ? 'true' : 'false'}
                                                        aria-controls={`report-${report.id}`}>
                                                        {report.name}
                                                    </button>
                                                </h2>
                                                <div id={`report-${report.id}`}
                                                    className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                                                    aria-labelledby={`heading-${report.id}`} data-bs-parent="#reportAccordion">
                                                    <div className="accordion-body" style={{ maxWidth: '100%' }}>
                                                        <div className='d-flex justify-content-between align-items-center'>
                                                            <h5 className='mb-0'>Tabs</h5>
                                                            {
                                                                canEdit && (
                                                                    <div className='d-flex gap-2'>
                                                                        {report.submenus.length !== 0 &&
                                                                            <button
                                                                                className="action-btn d-flex align-items-center justify-content-center"
                                                                                onClick={() => {
                                                                                    setMenuId(report.id);
                                                                                    setIsEditTabModalOpen(true)
                                                                                }}
                                                                            >
                                                                                <KTSVG path='/media/map/settings.svg' className='svg-icon-1' />
                                                                            </button>}
                                                                        <button
                                                                            className="action-btn d-flex align-items-center justify-content-center"
                                                                            onClick={() => {
                                                                                setMenuId(report.id);
                                                                                setIsAddTabModalOpen(true)

                                                                            }}
                                                                        >
                                                                            <KTSVG path='/media/map/zoom-in.svg' className='svg-icon-1 ' />
                                                                        </button>
                                                                    </div>)
                                                            }
                                                        </div>
                                                        <div className="mt-4">
                                                            {report.submenus.filter(tab => tab.isActive).length === 0 ? (
                                                                <div className='text-center mt-2 text-muted'>No tabs available</div>
                                                            ) : (
                                                                <div className="mt-3 p-4 border rounded-1" style={{ borderColor: '#D1D5DB' }}>
                                                                    {report.submenus.length > 0 && (report.submenus.filter(tab => tab.isActive).map((tab) => {
                                                                        const isActive = tab.id === submenuId; // ← define this inside the map
                                                                        return (
                                                                            <div
                                                                                key={tab.id}
                                                                                onClick={() => {
                                                                                    setReorderMode(false);
                                                                                    setDraggingFieldId(null);
                                                                                    setDragOverFieldId(null);
                                                                                    handleTabClick(tab.id);
                                                                                    setSubmenuId(tab.id);
                                                                                }}
                                                                                className={`mt-3 p-4 border rounded-1 ${isActive ? 'active-tab' : ''} hover-shadow`}
                                                                                style={{
                                                                                    backgroundColor: isActive ? '#e2e8f0' : '#F9FAFB',
                                                                                    borderColor: isActive ? '#1C325B' : '#D1D5DB',
                                                                                    cursor: 'pointer',
                                                                                    fontWeight: isActive ? '500' : 'normal'
                                                                                }}
                                                                            >
                                                                                {tab.name}
                                                                            </div>
                                                                        );
                                                                    }))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}

                            </div>
                            <div className='px-5 py-3 d-flex flex-column bg-white' style={{ flex: 3, minWidth: 0, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', paddingBottom: '3rem' }}>

                                {<>
                                        <div className='d-flex justify-content-between align-items-center mb-1 flex-shrink-0'>
                                            <h5 className="mb-0">Fields</h5>
                                            <div className="d-flex align-items-center gap-2">
                                                {canEdit && (
                                                    <button
                                                        className="action-btn d-flex align-items-center justify-content-center"
                                                        onClick={() => { setIsAddFieldModalOpen(true) }}
                                                    >
                                                        <KTSVG path='/media/map/zoom-in.svg' className='svg-icon-1' />
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-sm btn-light"
                                                    type="button"
                                                    disabled={activeFieldCount < 2 || isReorderSaving || !canReorderFields}
                                                    onClick={() => {
                                                        setReorderMode((prev) => !prev);
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    {isReorderSaving ? 'Saving...' : reorderMode ? 'Done' : 'Reorder'}
                                                </button>
                                            </div>
                                        </div>
                                        {reorderMode && (
                                            <div className="text-muted small mb-2 flex-shrink-0">
                                                Drag fields to rearrange. Changes save automatically.
                                            </div>
                                        )}
                                        <div className="report-table table-responsive" style={{ overflowX: 'auto', overflowY: 'visible', marginBottom: '1.5rem' }}>
                                            <table className="table table-bordered align-middle mb-0">
                                                <thead className="table-header">
                                                    <tr>
                                                        {reorderMode && <th rowSpan={2} style={{ width: '48px' }}>Move</th>}
                                                        <th rowSpan={2}>Label</th>
                                                        <th rowSpan={2}>Type</th>
                                                        <th rowSpan={2}>Required</th>
                                                        <th rowSpan={2}>Unit</th>
                                                        <th rowSpan={2}>Validation Type</th>
                                                        <th colSpan={2}>Validation Range</th>
                                                        {canEdit && (<th rowSpan={2}>Actions</th>)}
                                                    </tr>
                                                    <tr>
                                                        <th>Min Value</th>
                                                        <th>Max Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="table-body">
                                                    {loading ? (
                                                        <TableSkeleton rows={8} cols={emptyColSpan} />
                                                    ) : displayFields.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={emptyColSpan}>
                                                                <div className='text-center mt-4 text-muted'>No fields available</div>
                                                            </td>
                                                        </tr>
                                                    ) :
                                                        (displayFields.map((field) => {
                                                            const isDragging = draggingFieldId === field.id;
                                                            const isDragOver = dragOverFieldId === field.id;
                                                            return (
                                                                <tr
                                                                    key={field.id}
                                                                    draggable={reorderMode}
                                                                    onDragStart={() => handleDragStart(field.id)}
                                                                    onDragOver={(event) => handleDragOver(event, field.id)}
                                                                    onDrop={() => handleDrop(field.id)}
                                                                    onDragEnd={handleDragEnd}
                                                                    style={{
                                                                        opacity: isDragging ? 0.6 : 1,
                                                                        backgroundColor: isDragOver ? '#F4F7FF' : undefined,
                                                                        cursor: reorderMode ? 'move' : undefined,
                                                                    }}
                                                                >
                                                                    {reorderMode && (
                                                                        <td className="text-center">
                                                                            <span className="text-muted">::</span>
                                                                        </td>
                                                                    )}
                                                                    <td>{field.label}</td>
                                                                    <td>{field.fieldType}</td>
                                                                    <td>{field.required ? "Yes" : "No"}</td>
                                                                    <td>{field.unit}</td>
                                                                    <td>
                                                                        {field.warningOnly === "SOFT"
                                                                            ? "Soft"
                                                                            : field.warningOnly === "STRICT"
                                                                                ? "Strict"
                                                                                : null}
                                                                    </td>
                                                                    <td>{field.minValue}</td>
                                                                    <td>{field.maxValue}</td>
                                                                    {/* <td>
                                                            <span
                                                                className={`badge ${row.status === "Draft" ? "draft" : "submitted"
                                                                    }`}
                                                            >
                                                                {row.status}
                                                            </span>
                                                        </td> */}

                                                                    {
                                                                        canEdit && (
                                                                            <td>
                                                                                <button
                                                                                    onClick={() => {
                                                                                        setSelectedField(field);
                                                                                        setIsEditFieldModalOpen(true);
                                                                                    }}
                                                                                    className="btn btn-sm px-0"
                                                                                >
                                                                                    <KTSVG path='/media/map/edit-active.svg' className='' />
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => {
                                                                                        setSelectedField(field);
                                                                                        setIsDeleteFieldModalOpen(true);
                                                                                    }}
                                                                                    className="btn btn-sm px-0">
                                                                                    <KTSVG path='/media/map/trash.svg' className='' />
                                                                                </button>
                                                                            </td>
                                                                        )
                                                                    }

                                                                </tr>
                                                            )
                                                        }))}
                                                </tbody>
                                            </table>
                                        </div>
                                        {/* Pagination */}
                                        {!reorderMode && !loading && displayFields.length > 0 && (
                                            <div className="pagination-wrapper d-flex justify-content-between align-items-center mt-auto pb-4 flex-shrink-0">
                                                <div>
                                                    Rows per page
                                                    <SearchableSelect
                                                        className="form-select d-inline-block w-auto ms-2"
                                                        style={{ borderRadius: "20px" }}
                                                        value={rowsPerPage}
                                                        onChange={(e) => {
                                                            setRowsPerPage(Number(e.target.value));
                                                            setCurrentPage(1); // reset to first page when changing rows per page
                                                        }}
                                                    >
                                                        <option value={10}>10</option>
                                                        <option value={20}>20</option>
                                                        <option value={50}>50</option>
                                                    </SearchableSelect>
                                                </div>

                                                <nav>
                                                    <ul className="pagination mb-0">
                                                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => goToPage(currentPage - 1)}>
                                                                ‹
                                                            </button>
                                                        </li>
                                                        {Array.from({ length: totalPages }, (_, i) => (
                                                            <li key={i + 1} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                                                                <button className="page-link" onClick={() => goToPage(i + 1)}>{i + 1}</button>
                                                            </li>
                                                        ))}

                                                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                                            <button className="page-link" onClick={() => goToPage(currentPage + 1)}>›</button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        )}
                                </>}
                            </div>
                        </div>
                    </div>
                    {isAddReportModalOpen && (
                        <QhseAddReportModal
                            isOpen={isAddReportModalOpen}
                            onClose={() => { setIsAddReportModalOpen(false) }}
                            onReportAdded={onReportAdded}
                            templateContext={{
                                companyGroupAdminId: resolveCompanyGroupId(),
                                companyAdminId: resolveCompanyAdminId(),
                            }}
                        />
                    )}
                    {isEditReportModalOpen && (
                        <QhseEditReportModal
                            isOpen={isEditReportModalOpen}
                            onClose={() => { setIsEditReportModalOpen(false) }}
                            onReportAdded={fetchReportsList}
                            templateContext={{
                                companyGroupAdminId: resolveCompanyGroupId(),
                                companyAdminId: resolveCompanyAdminId(),
                            }}
                        />
                    )}
                    {isAddTabModalOpen && (
                        <QhseAddTabModal
                            isOpen={isAddTabModalOpen}
                            onClose={() => { setIsAddTabModalOpen(false) }}
                            menuId={menuId}
                            onTabAdded={fetchReportsList}
                            allowConsumptionTab={libraryApi.features.allowConsumptionTab}
                        />
                    )}
                    {isEditTabModalOpen && (
                        <QhseEditTabModal
                            isOpen={isEditTabModalOpen}
                            onClose={() => { setIsEditTabModalOpen(false) }}
                            menuId={menuId}
                            onTabAdded={fetchReportsList}
                            templateContext={{
                                companyGroupAdminId: resolveCompanyGroupId(),
                                companyAdminId: resolveCompanyAdminId(),
                            }}
                        />
                    )}
                    {isAddFieldModalOpen && (
                        <QhseAddFieldModal
                            isOpen={isAddFieldModalOpen}
                            onClose={() => { setIsAddFieldModalOpen(false) }}
                            submenuId={submenuId}
                            onFieldAdded={handleFieldAdded}
                        />
                    )}
                    {isEditFieldModalOpen && (
                        <QhseEditFieldModal
                            isOpen={isEditFieldModalOpen}
                            onClose={() => { setIsEditFieldModalOpen(false) }}
                            fieldData={selectedField}
                            onFieldEdited={handleFieldAdded}
                        />
                    )}
                    {isDeleteFieldModalOpen && (
                        <QhseDeleteFieldModal
                            isOpen={isDeleteFieldModalOpen}
                            onClose={() => { setIsDeleteFieldModalOpen(false) }}
                            submenuId={submenuId}
                            fieldId={selectedField ? selectedField.id : 0}
                            labelName={selectedField ? selectedField.label : ""}
                            onFieldDeleted={handleFieldAdded}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

export default QhseLibraryLayout

